/**
 * Lot 1a — sélection métiers (UI) sans fallback silencieux RESTAURANT.
 */
import {
  normalizeCategorieId,
  getDefaultsForCategories,
  getCommerceMetier,
} from '../config/commerce-categories.js';
import { getDefaultsFromMetiers } from './taxonomyDefaults.js';

export const EMPTY_CATEGORY_SELECTION = Object.freeze({
  categorie: '',
  categories: Object.freeze([]),
});

export const MSG_CATEGORIES_REQUIRED =
  'Sélectionnez au moins un métier avant d’enregistrer.';

export const MSG_PRIMARY_NOT_IN_SELECTION =
  'Le métier principal doit faire partie des métiers sélectionnés.';

/**
 * IDs sélectionnés depuis value/values — jamais de RESTAURANT inventé.
 * @param {string} [value]
 * @param {string[]} [values]
 * @returns {string[]}
 */
export function resolveSelectedCategoryIds(value, values) {
  if (Array.isArray(values)) {
    return [...new Set(values.map(normalizeCategorieId).filter(Boolean))];
  }
  const primary = normalizeCategorieId(value);
  return primary ? [primary] : [];
}

/**
 * Normalise principal + liste. Sélection vide autorisée (categorie '').
 * @param {{ categorie?: string, categories?: string[] }} input
 * @returns {{ categorie: string, categories: string[] }}
 */
export function normalizeCategorySelection({ categorie, categories } = {}) {
  const cats = resolveSelectedCategoryIds(categorie, categories);
  let primary = normalizeCategorieId(categorie);
  if (primary && !cats.includes(primary)) {
    primary = cats[0] || '';
  }
  if (!primary && cats.length) {
    primary = cats[0];
  }
  if (!cats.length) {
    return { categorie: '', categories: [] };
  }
  return { categorie: primary, categories: cats };
}

/**
 * Émission onChange CategoryPicker.
 * @param {string[]} nextIds
 * @param {string} [nextPrimary]
 * @param {{ metiers?: object[], getMetierFn?: (id: string) => object|null }} [options]
 */
export function buildCategoryChangePayload(nextIds, nextPrimary, options = {}) {
  const { metiers, getMetierFn } = options;
  const normalized = normalizeCategorySelection({
    categorie: nextPrimary,
    categories: nextIds,
  });
  const lookupMetier = getMetierFn || ((id) => getCommerceMetier(id));
  const defaults = Array.isArray(metiers) && metiers.length
    ? getDefaultsFromMetiers(metiers, normalized.categories)
    : getDefaultsForCategories(normalized.categories);
  return {
    ...normalized,
    metier: normalized.categorie ? lookupMetier(normalized.categorie) : null,
    defaults,
  };
}

/**
 * @param {{ categorie?: string, categories?: string[] }} selection
 * @returns {{ ok: true }|{ ok: false, error: string }}
 */
export function validateCategorySelection(selection) {
  const { categorie, categories } = normalizeCategorySelection(selection);
  if (!categories.length) {
    return { ok: false, error: MSG_CATEGORIES_REQUIRED };
  }
  if (!categorie || !categories.includes(categorie)) {
    return { ok: false, error: MSG_PRIMARY_NOT_IN_SELECTION };
  }
  return { ok: true };
}

/**
 * Comparaison stable pour dirty-check édition.
 */
export function categorySelectionsEqual(a, b) {
  const na = normalizeCategorySelection(a);
  const nb = normalizeCategorySelection(b);
  if (na.categorie !== nb.categorie) return false;
  if (na.categories.length !== nb.categories.length) return false;
  const sb = new Set(nb.categories);
  return na.categories.every((id) => sb.has(id));
}

/**
 * Champs métiers pour PUT édition : uniquement si l’utilisateur a modifié le picker.
 * @returns {{ categorie?: string, categories?: string[] }}
 */
export function buildEditCategoriesPayload({ dirty, categorie, categories }) {
  if (!dirty) return {};
  const normalized = normalizeCategorySelection({ categorie, categories });
  return {
    categorie: normalized.categorie,
    categories: normalized.categories,
  };
}

/**
 * Métiers sélectionnés hors de la famille cible (pour confirmation changement famille).
 * @param {string[]} selectedIds
 * @param {string} targetFamilyId
 * @param {(id: string) => object|null} [getMetierFn]
 * @returns {string[]}
 */
export function selectedIdsOutsideFamily(selectedIds, targetFamilyId, getMetierFn) {
  const lookup = getMetierFn || ((id) => getCommerceMetier(id));
  return (selectedIds || []).filter((id) => {
    const m = lookup(id);
    const fam = m?.familyId || m?.family;
    return fam && fam !== targetFamilyId;
  });
}
